import { LocalShipping as LocalShippingIcon } from "@mui/icons-material";
import { Box, Switch, Tooltip, Typography } from "@mui/material";
import React from "react";
import { Vehicle } from "../../../../api/vehiclesApi";
import { useHosProfiles } from "../../hooks/useHosProfiles";
import { useSidebarInfiniteScroll } from "../../hooks/useSidebarInfiniteScroll";
import { useVehicleFilter } from "../../hooks/useVehicleFilter";
import { SidebarShell } from "./SidebarShell";
import { VehicleList } from "./VehicleList";

interface VehicleSidebarProps {
  vehicles: Vehicle[];
  selectedVehicleId?: number;
  onVehicleSelect: (vehicleId: number) => void;
  isLoading?: boolean;
  showVehicles?: boolean;
  onShowVehiclesToggle?: () => void;
  onSidebarToggle?: (isOpen: boolean) => void;
  defaultOpen?: boolean;
}

const VEHICLE_SIDEBAR_WIDTH = 280;

export const VehicleSidebar: React.FC<VehicleSidebarProps> = ({
  vehicles,
  selectedVehicleId,
  onVehicleSelect,
  isLoading = false,
  showVehicles = true,
  onShowVehiclesToggle,
  onSidebarToggle,
  defaultOpen = true,
}) => {
  const { profiles, isLoading: isHosLoading } = useHosProfiles();
  const { searchQuery, setSearchQuery, filteredVehicles } = useVehicleFilter({
    vehicles,
  });
  const {
    displayedItems: displayedVehicles,
    totalCount,
    hasMore,
    isLoadingMore,
    sentinelRef,
    listRef,
  } = useSidebarInfiniteScroll(filteredVehicles, undefined, searchQuery);

  const isLoadingData = isLoading || isHosLoading;

  const headerExtra = onShowVehiclesToggle ? (
    <Tooltip title="Show vehicles on map">
      <Box
        component="span"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.25,
          px: 0.5,
          borderRadius: 1,
          border: 1,
          borderColor: showVehicles ? "primary.main" : "divider",
          bgcolor: showVehicles ? "primary.light" : "background.paper",
          flexShrink: 0,
        }}
      >
        <LocalShippingIcon
          sx={{
            fontSize: "0.85rem",
            color: showVehicles ? "primary.dark" : "text.secondary",
          }}
        />
        <Typography
          variant="caption"
          sx={{
            color: showVehicles ? "primary.dark" : "text.secondary",
            fontWeight: 600,
            fontSize: "0.65rem",
            lineHeight: 1,
            whiteSpace: "nowrap",
          }}
        >
          Show
        </Typography>
        <Switch
          size="small"
          checked={showVehicles}
          onChange={onShowVehiclesToggle}
          disabled={isLoadingData}
          inputProps={{ "aria-label": "Show vehicles on map" }}
          sx={{
            ml: -0.5,
            "& .MuiSwitch-switchBase.Mui-checked": {
              color: "primary.main",
              "& + .MuiSwitch-track": {
                backgroundColor: "primary.main",
              },
            },
          }}
        />
      </Box>
    </Tooltip>
  ) : null;

  return (
    <SidebarShell
      title="Vehicles"
      icon={<LocalShippingIcon sx={{ fontSize: 15 }} />}
      totalCount={totalCount}
      displayedCount={displayedVehicles.length}
      searchQuery={searchQuery}
      onSearchChange={setSearchQuery}
      searchPlaceholder="Search vehicles..."
      isLoading={isLoadingData}
      isLoadingMore={isLoadingMore}
      width={VEHICLE_SIDEBAR_WIDTH}
      toggleButtonTop="8%"
      toggleButtonColor="primary"
      onSidebarToggle={onSidebarToggle}
      defaultOpen={defaultOpen}
      headerExtra={headerExtra}
    >
      <VehicleList
        vehicles={displayedVehicles}
        selectedVehicleId={selectedVehicleId ?? null}
        onVehicleSelect={onVehicleSelect}
        hosProfiles={profiles}
        isLoading={isLoadingData}
        sentinelRef={sentinelRef}
        listRef={listRef}
        isLoadingMore={isLoadingMore}
        hasMore={hasMore}
      />
    </SidebarShell>
  );
};
